import React from 'react'
import { motion } from 'framer-motion'
import { 
  Calendar, 
  Users, 
  Target, 
  Award, 
  TrendingUp, 
  Globe,
  Building,
  Briefcase,
  Star,
  CheckCircle
} from 'lucide-react'

const Experience: React.FC = () => {
  const experience = [
    {
      company: "Xicom Technologies Ltd",
      position: "Head of Operations",
      duration: "Oct 2021 - Present",
      location: "Noida, India",
      description: "Leading cross-functional teams across 50+ software projects, driving operational excellence and strategic growth through Agile implementation and strategic resource allocation.",
      achievements: [
        "Directed cross-functional teams across 50+ software projects, improving delivery timelines by 25%",
        "Oversaw project governance, budgeting, and compliance, reducing operational risks by 30%",
        "Built scalable IT infrastructure and DevOps pipelines that increased development efficiency and system reliability",
        "Mentored 10+ project managers, standardizing PM best practices and boosting project success rate by 35%",
        "Drove alignment between delivery and business goals, ensuring consistent client satisfaction and repeat engagements"
      ],
      technologies: ["Agile", "Scrum", "Project Governance", "Resource Management", "DevOps", "Jira", "Confluence", "Strategic Planning"],
      impact: "25% faster delivery, 30% risk reduction, 35% success rate improvement, enhanced system reliability",
      category: "Operations Leadership"
    },
    {
      company: "Xicom Technologies Ltd",
      position: "Project Delivery Manager",
      duration: "Jun 2016 - Oct 2021",
      location: "Noida, India",
      description: "Ensured projects aligned with company strategic goals, executed efficiently and effectively through comprehensive project management and stakeholder communication.",
      achievements: [
        "Oversaw project planning process aligned with company strategic goals and feasibility",
        "Managed project budgets, tracked expenses, and identified cost-saving opportunities",
        "Identified and mitigated project risks, developing contingency plans for unexpected events",
        "Maintained stakeholder communication, addressing concerns and keeping them informed of progress",
        "Ensured compliance with relevant laws and regulations throughout project lifecycle",
        "Implemented tools like Jira and Confluence for efficient execution and senior management visibility"
      ],
      technologies: ["Project Planning", "Budget Management", "Risk Management", "Stakeholder Communication", "Compliance", "Jira", "Confluence", "Strategic Alignment"],
      impact: "Improved project predictability, enhanced strategic decision-making, maintained compliance standards",
      category: "Project Delivery Management"
    },
    {
      company: "Xicom Technologies Ltd",
      position: "Senior Project Manager",
      duration: "Jun 2013 - May 2016",
      location: "Noida, India",
      description: "Led cross-functional teams using Agile methodologies to deliver high-quality web and mobile applications, optimizing project efficiency and fostering continuous improvement.",
      achievements: [
        "Led cross-functional teams of developers, designers, and QA analysts for successful project delivery",
        "Managed project schedules, budgets, and resources with Agile project management tools",
        "Coordinated with product owners and stakeholders to gather requirements and prioritize tasks",
        "Facilitated Agile ceremonies including daily stand-ups, sprint planning, retrospectives, and demos",
        "Collaborated with senior management to establish and enhance Agile processes organization-wide",
        "Delivered high-quality software solutions that exceeded client expectations"
      ],
      technologies: ["Agile", "Scrum", "Team Leadership", "Project Management", "Stakeholder Management", "Agile Ceremonies", "Process Improvement"],
      impact: "Timely delivery, high client satisfaction, optimized project efficiency, minimized waste",
      category: "Agile Project Management"
    },
    {
      company: "Flexsin Inc.",
      position: "Project Manager",
      duration: "Sep 2010 - May 2013",
      location: "Remote",
      description: "Managed department P&L, client relations, and complete SDLC for international projects including Coca-Cola, OCC Switzerland, and Bluehouse-Dubai applications.",
      achievements: [
        "Successfully delivered over 30 projects including web development, application development, iOS and Android apps",
        "Managed project budgets and timelines ensuring on-time and within-budget delivery",
        "Led cross-functional teams to ensure effective collaboration and project success",
        "Handled international clients including Coca-Cola, OCC Switzerland, and Bluehouse-Dubai",
        "Managed department profit statements and performance metrics",
        "Provided sales support and maintained high client retention and satisfaction rates"
      ],
      technologies: ["P&L Management", "Client Relations", "SDLC", "Performance Management", "iOS Development", "Android Development", "Web Development"],
      impact: "30+ projects delivered, increased client satisfaction, maintained high retention rates",
      category: "International Project Management"
    },
    {
      company: "Greenrev Ventures Private Limited",
      position: "Enterprise Manager",
      duration: "Nov 2008 - Apr 2011",
      location: "India",
      description: "Accountable for business solutions strategy and operations, leading multi-skilled teams and managing process re-engineering for improved efficiency and ROI.",
      achievements: [
        "Accountable for business solutions strategy and operations ensuring proper development and ROI",
        "Developed and led multi-skilled teams to ensure timely project delivery",
        "Organized structured training programs to enhance skill sets and create awareness of latest developments",
        "Managed process re-engineering, development of processes and policies",
        "Evaluated and implemented audit and risk recommendations",
        "Successfully led implementation of processes and policies resulting in improved efficiency and reduced costs"
      ],
      technologies: ["Business Strategy", "Team Leadership", "Process Re-engineering", "Training Programs", "Audit Management", "Risk Management"],
      impact: "Improved efficiency, reduced costs, enhanced team skill sets, successful project delivery",
      category: "Business Solutions Management"
    },
    {
      company: "Convergys India Pvt Ltd",
      position: "Care Executive (Sales and Marketing)",
      duration: "May 2002 - Jun 2005",
      location: "India",
      description: "Managed customer service and sales for AT&T group within Customer Sales and Services (CSSC) team, providing floor supervision and ensuring high-quality customer interactions.",
      achievements: [
        "Managed customer service and sales email and voice channels for AT&T group",
        "Provided floor supervision ensuring all customer concerns were addressed promptly",
        "Maintained high levels of quality in customer interactions",
        "Built and maintained positive relationships with customers",
        "Demonstrated strong attention to detail and excellent communication skills",
        "Managed both sales and customer service operations effectively"
      ],
      technologies: ["Customer Service", "Sales Management", "Team Supervision", "Communication Skills", "Quality Management", "AT&T Systems"],
      impact: "High customer satisfaction, improved service quality, effective team supervision",
      category: "Customer Service & Sales"
    }
  ]

  const keyAchievements = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Team Leadership",
      description: "Led 50+ cross-functional team members across diverse projects",
      metric: "50+ Team Members"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Project Delivery",
      description: "Successfully delivered 100+ software projects across multiple sectors",
      metric: "100+ Projects"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Performance Improvement",
      description: "Achieved 25% faster delivery and 30% risk reduction",
      metric: "25% Faster Delivery"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Career Growth",
      description: "20+ years of progressive experience from Care Executive to Head of Operations",
      metric: "20+ Years"
    }
  ]

  const skills = [
    "Agile Project Management", "Scrum", "PRINCE2", "Project Governance",
    "Resource Management", "DevOps", "Mobile Development", "Full-Stack Development",
    "AI/ML Integration", "Blockchain", "Team Leadership", "Process Optimization",
    "Client Relations", "P&L Management", "Risk Management", "Quality Assurance",
    "Strategic Planning", "Budget Management", "Stakeholder Communication", "Compliance",
    "Process Re-engineering", "Training Programs", "Audit Management", "Business Strategy"
  ]

  return (
    <section id="experience" className="section-padding bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Professional <span className="gradient-text">Experience</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            A proven track record of leadership, project delivery, and operational excellence across diverse technology sectors with over 20 years of progressive experience
          </p>
        </motion.div>

        {/* Key Achievements Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyAchievements.map((achievement, index) => (
              <motion.div
                key={achievement.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:border-cyber-500/30"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="p-3 bg-gradient-to-r from-cyber-500 to-cyber-600 rounded-lg text-white">
                    {achievement.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg">
                      {achievement.title}
                    </h3>
                    <p className="text-2xl font-bold text-cyber-600 dark:text-cyber-400">
                      {achievement.metric}
                    </p>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {achievement.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Experience Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-12 text-center">
            Work <span className="gradient-text">History</span>
          </h3>
          
          <div className="space-y-8">
            {experience.map((job, index) => (
              <motion.div
                key={`${job.company}-${job.position}`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`flex flex-col lg:flex-row gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Company Info */}
                <div className="lg:w-1/3">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-3 mb-4">
                      <Building className="w-6 h-6 text-cyber-500" />
                      <h4 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                        {job.company}
                      </h4>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Briefcase className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold text-gray-700 dark:text-gray-300">
                          {job.position}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600 dark:text-gray-400">
                          {job.duration}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Globe className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600 dark:text-gray-400">
                          {job.location}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <span className="px-3 py-1 bg-cyber-100 dark:bg-cyber-900/30 text-cyber-700 dark:text-cyber-300 text-sm font-medium rounded-full">
                        {job.category}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Job Details */}
                <div className="lg:w-2/3">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed text-lg">
                      {job.description}
                    </p>

                    {/* Key Achievements */}
                    <div className="mb-6">
                      <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <Star className="w-4 h-4 mr-2 text-yellow-500" />
                        Key Achievements
                      </h5>
                      <ul className="space-y-2">
                        {job.achievements.map((achievement, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Impact & Technologies */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                          <TrendingUp className="w-4 h-4 mr-2 text-green-500" />
                          Impact
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                          {job.impact}
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                          <Target className="w-4 h-4 mr-2 text-blue-500" />
                          Technologies
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {job.technologies.map((tech) => (
                            <span
                              key={tech}
                              className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-md border border-blue-200 dark:border-blue-800"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Core Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-8 text-center">
            Core <span className="gradient-text">Skills</span>
          </h3>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-3 justify-center">
              {skills.map((skill, index) => (
                <motion.span
                  key={skill}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="px-4 py-2 bg-gradient-to-r from-cyber-500/10 to-cyber-600/10 text-cyber-700 dark:text-cyber-300 rounded-full border border-cyber-200 dark:border-cyber-700 hover:from-cyber-500/20 hover:to-cyber-600/20 transition-all duration-300 cursor-default"
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Experience
